import { useCallback, useState } from 'react';
import type { Location, NavigateFunction } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';

import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from '@/components/ui/sidebar';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { HoverPopover } from '@/components/ui/popover';
import type { HasMetaRouteWithFullPath } from '@/router';
import { Button } from '@/components/ui/button';

interface NavMainProps {
  routes: HasMetaRouteWithFullPath[];
  location: Location;
  navigate: NavigateFunction;
}

export function NavMain({ routes, location, navigate }: NavMainProps) {
  // 管理每个菜单项的展开状态
  const [openItems, setOpenItems] = useState<Record<string, boolean>>({});
  const { state, isMobile } = useSidebar(); // 获取侧边栏状态和移动端状态

  // 将动态路由模式转换为正则表达式进行匹配
  const matchDynamicRoute = useCallback((routePath: string, currentPath: string): boolean => {
    // 将路由模式中的 :param 转换为正则表达式
    const pattern = routePath.replace(/:[^/]+/g, '[^/]+');
    const regex = new RegExp(`^${pattern}$`);
    return regex.test(currentPath);
  }, []);

  // 判断路径是否匹配（支持精确匹配、前缀匹配和动态路由匹配）
  const isPathMatch = useCallback(
    (routePath: string, currentPath: string): boolean => {
      // 精确匹配
      if (currentPath === routePath) return true;
      // 动态路由匹配
      if (routePath.includes(':') && matchDynamicRoute(routePath, currentPath)) return true;
      // 前缀匹配：当前路径以路由路径开头（确保是完整的路径段）
      if (currentPath.startsWith(routePath + '/')) return true;
      return false;
    },
    [matchDynamicRoute]
  );

  // 判断菜单项是否激活
  const isActive = useCallback(
    (url: string) => {
      return isPathMatch(url, location.pathname);
    },
    [location.pathname, isPathMatch]
  );

  // 递归收集所有子路由的路径
  const collectAllChildPaths = useCallback((route: HasMetaRouteWithFullPath): string[] => {
    const paths: string[] = [];

    const traverse = (r: HasMetaRouteWithFullPath) => {
      if (r.fullPath) {
        paths.push(r.fullPath);
      }
      if (r.children) {
        // 过滤出有meta的子路由
        r.children.forEach((child) => {
          if (child.meta) {
            traverse(child as HasMetaRouteWithFullPath);
          }
        });
      }
    };

    if (route.children) {
      // 过滤出有meta的子路由
      route.children.forEach((child) => {
        if (child.meta) {
          traverse(child as HasMetaRouteWithFullPath);
        }
      });
    }

    return paths;
  }, []);

  // 判断父菜单是否有子项处于激活状态（递归检查所有层级）
  const hasActiveChild = useCallback(
    (route: HasMetaRouteWithFullPath): boolean => {
      if (!route.children) return false;

      // 收集所有子路由路径
      const allChildPaths = collectAllChildPaths(route);

      // 检查当前路径是否匹配任何子路由路径
      return allChildPaths.some((childPath) => isPathMatch(childPath, location.pathname));
    },
    [location.pathname, collectAllChildPaths, isPathMatch]
  );

  // 处理菜单点击
  const handleMenuClick = useCallback(
    (url: string) => {
      navigate(url);
    },
    [navigate]
  );

  // 切换菜单展开状态
  const toggleItem = useCallback((key: string) => {
    setOpenItems((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  }, []);

  return (
    <SidebarGroup>
      <SidebarGroupContent>
        <SidebarMenu>
          {routes.map((route) => {
            const hasChildren = route.children && route.children.length > 0;
            const isParentActive = hasActiveChild(route);
            const itemKey = route.fullPath || route.meta.title;
            const Icon = route.meta.icon;

            if (hasChildren) {
              // 只在桌面端折叠状态下使用HoverPopover，移动端抽屉使用正常的展开模式
              if (state === 'collapsed' && !isMobile) {
                const popoverContent = (
                  <div className="space-y-1">
                    {/* <div className="px-2 py-1 text-sm font-medium text-muted-foreground">
                      {route.meta.title}
                    </div> */}
                    {route.children!.map((subRoute) => {
                      if (!subRoute.meta) return null;
                      const subUrl = subRoute.fullPath;
                      return (
                        <Button
                          size="sm"
                          variant={isActive(subUrl) ? 'default' : 'ghost'}
                          key={subUrl}
                          onClick={() => handleMenuClick(subUrl)}
                          className="w-full px-2 py-1.5 text-sm justify-normal"
                        >
                          {subRoute.meta.title}
                        </Button>
                      );
                    })}
                  </div>
                );

                return (
                  <SidebarMenuItem key={itemKey}>
                    <HoverPopover
                      content={popoverContent}
                      side="right"
                      align="start"
                      sideOffset={8}
                      contentClassName="w-48 p-2"
                      delayDuration={300}
                    >
                      <SidebarMenuButton
                        className="group-data-[collapsible=compact]:flex-col group-data-[collapsible=compact]:items-center group-data-[collapsible=compact]:justify-center group-data-[collapsible=compact]:gap-1 group-data-[collapsible=compact]:py-2 group-data-[collapsible=compact]:px-2 group-data-[collapsible=compact]:text-xs group-data-[collapsible=compact]:h-15"
                        isActive={isParentActive}
                        tooltip={route.meta.title}
                        onClick={() => handleMenuClick(route.fullPath)}
                      >
                        {Icon && <Icon className="h-4 w-4" />}
                        <span className="group-data-[collapsible=compact]:text-center group-data-[collapsible=compact]:leading-tight">
                          {route.meta.title}
                        </span>
                      </SidebarMenuButton>
                    </HoverPopover>
                  </SidebarMenuItem>
                );
              }

              // 展开模式下的原有逻辑
              return (
                <Collapsible
                  key={itemKey}
                  open={openItems[itemKey] || isParentActive}
                  onOpenChange={() => toggleItem(itemKey)}
                >
                  <SidebarMenuItem>
                    <CollapsibleTrigger asChild>
                      <SidebarMenuButton
                        className="text-sidebar-foreground text-nowrap"
                        isActive={isParentActive}
                        isRootMenu={false}
                      >
                        {Icon && <Icon className="h-4 w-4" />}
                        <span>{route.meta.title}</span>
                        <ChevronRight
                          className={`ml-auto h-4 w-4 transition-transform ${
                            openItems[itemKey] || isParentActive ? 'rotate-90' : ''
                          }`}
                        />
                      </SidebarMenuButton>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <SidebarMenuSub>
                        {route.children!.map((subRoute) => {
                          if (!subRoute.meta) return null;
                          const subUrl = subRoute.fullPath;
                          const AsideBarWrapper = subRoute.meta.asideBarWrapper;
                          return (
                            <SidebarMenuSubItem key={subUrl}>
                              {AsideBarWrapper ? (
                                <AsideBarWrapper>
                                  <SidebarMenuSubButton
                                    isActive={isActive(subUrl)}
                                    onClick={() => handleMenuClick(subUrl)}
                                  >
                                    <span>{subRoute.meta.title}</span>
                                  </SidebarMenuSubButton>
                                </AsideBarWrapper>
                              ) : (
                                <SidebarMenuSubButton
                                  isActive={isActive(subUrl)}
                                  onClick={() => handleMenuClick(subUrl)}
                                >
                                  <span>{subRoute.meta.title}</span>
                                </SidebarMenuSubButton>
                              )}
                            </SidebarMenuSubItem>
                          );
                        })}
                      </SidebarMenuSub>
                    </CollapsibleContent>
                  </SidebarMenuItem>
                </Collapsible>
              );
            }

            // 无子菜单的一级菜单项
            const url = route.fullPath;
            const AsideBarWrapper = route.meta.asideBarWrapper;
            return (
              <SidebarMenuItem key={itemKey}>
                {AsideBarWrapper ? (
                  <AsideBarWrapper>
                    <SidebarMenuButton
                      className="group-data-[collapsible=compact]:flex-col group-data-[collapsible=compact]:items-center group-data-[collapsible=compact]:justify-center group-data-[collapsible=compact]:gap-1 group-data-[collapsible=compact]:py-2 group-data-[collapsible=compact]:px-2 group-data-[collapsible=compact]:text-xs group-data-[collapsible=compact]:h-15"
                      tooltip={route.meta.title}
                      isActive={isActive(url)}
                      onClick={() => handleMenuClick(url)}
                    >
                      {Icon && <Icon />}
                      <span className="group-data-[collapsible=compact]:text-center group-data-[collapsible=compact]:leading-tight">
                        {route.meta.title}
                      </span>
                    </SidebarMenuButton>
                  </AsideBarWrapper>
                ) : (
                  <SidebarMenuButton
                    className="group-data-[collapsible=compact]:flex-col group-data-[collapsible=compact]:items-center group-data-[collapsible=compact]:justify-center group-data-[collapsible=compact]:gap-1 group-data-[collapsible=compact]:py-2 group-data-[collapsible=compact]:px-2 group-data-[collapsible=compact]:text-xs group-data-[collapsible=compact]:h-15"
                    tooltip={route.meta.title}
                    isActive={isActive(url)}
                    onClick={() => handleMenuClick(url)}
                  >
                    {Icon && <Icon />}
                    <span className="group-data-[collapsible=compact]:text-center group-data-[collapsible=compact]:leading-tight">
                      {route.meta.title}
                    </span>
                  </SidebarMenuButton>
                )}
              </SidebarMenuItem>
            );
          })}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
}
